import { HttpStatusError } from "@/api/classifier";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import type { SaveState } from "./saveState";
import { useEncounterHumanReviewMutations } from "./useEncounterHumanReviewMutations";

type CodeInput = {
  code: string;
  description?: string;
};

type DiagnosisInput = CodeInput & {
  isComorbidity?: boolean;
  isOptional?: boolean;
  orderIndex: number;
};

type ProcedureInput = CodeInput & {
  orderIndex: number;
};

function buildPayload({
  encounterId,
  diagnoses,
  procedures,
}: {
  encounterId: string;
  diagnoses: CodeInput[];
  procedures: CodeInput[];
}) {
  return {
    encounterId,
    procedures: procedures.map((p, index) => ({
      opcs_4_code: p.code,
      description: p.description || "",
      order_index: index,
    })),
    diagnoses: diagnoses.map((d, index) => ({
      icd_10_code: d.code,
      description: d.description || "",
      order_index: index,
    })),
  };
}

function deriveSaveState({
  signature,
  savedSignature,
  inFlightSignature,
  strandedSignature,
  closedToCodeChanges,
  hasSaved,
}: {
  signature: string;
  savedSignature: string;
  inFlightSignature: string | null;
  strandedSignature: string | null;
  closedToCodeChanges: boolean;
  hasSaved: boolean;
}): SaveState {
  if (inFlightSignature === signature) return "saving";

  const dirty = signature !== savedSignature;
  if (dirty && (closedToCodeChanges || strandedSignature === signature)) return "stalled";
  if (dirty) return "unsaved";
  if (hasSaved) return "saved";
  return "idle";
}

export function useAutoSaveReviewCodes({
  encounterId,
  diagnoses,
  procedures,
  baselineDiagnoses,
  baselineProcedures,
  enabled = true,
  debounceMs = 1000,
}: {
  encounterId: string;
  diagnoses: DiagnosisInput[];
  procedures: ProcedureInput[];
  baselineDiagnoses?: CodeInput[];
  baselineProcedures?: CodeInput[];
  enabled?: boolean;
  debounceMs?: number;
}) {
  const { saveCodes } = useEncounterHumanReviewMutations();

  // State, not refs: these four are read while deriving the reported state during render.
  const [inFlightSignature, setInFlightSignature] = useState<string | null>(null);
  const [savedSignature, setSavedSignature] = useState("");
  const [hasSaved, setHasSaved] = useState(false);
  const [strandedSignature, setStrandedSignature] = useState<string | null>(null);
  // The encounter, not the payload: a refusal reports the review's state, which no edit changes.
  // Held rather than cleared, so returning to a refused encounter is still refused, while opening
  // another one simply does not match.
  const [closedEncounterId, setClosedEncounterId] = useState<string | null>(null);
  // Counted rather than flagged so it cannot be cleared by one request while another is outstanding.
  const [outstandingSaves, setOutstandingSaves] = useState(0);
  // A monotonic counter, not a boolean: suspend+resume in one batch must still re-arm the effect.
  const [scheduleNonce, setScheduleNonce] = useState(0);

  const lastSignatureRef = useRef<string>("");
  const suspendedRef = useRef(false);
  const suspendedEncounterIdRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);
  const retriedSignatureRef = useRef<string | null>(null);
  const openAttemptRef = useRef({ encounterId, signature: "" });

  // The ref is read synchronously inside the debounce, where state would be a render stale; the state
  // is what the badge derives from. One writer so they cannot drift apart.
  const commitSavedSignature = useCallback((committed: string) => {
    lastSignatureRef.current = committed;
    setSavedSignature(committed);
  }, []);

  // The encounter can change under a live hook instance, so a once-only seed would go stale.
  const seededEncounterIdRef = useRef<string | null>(null);
  if (baselineDiagnoses && baselineProcedures && seededEncounterIdRef.current !== encounterId) {
    seededEncounterIdRef.current = encounterId;
    const baselineSignature = JSON.stringify(
      buildPayload({ encounterId, diagnoses: baselineDiagnoses, procedures: baselineProcedures }),
    );
    commitSavedSignature(baselineSignature);
    setHasSaved(false);
    setStrandedSignature(null);
  }
  const hasBaseline = Boolean(baselineDiagnoses && baselineProcedures);
  const closedToCodeChanges = closedEncounterId === encounterId;

  const normalizedPayload = useMemo(
    () => buildPayload({ encounterId, diagnoses, procedures }),
    [encounterId, diagnoses, procedures],
  );

  const signature = useMemo(() => JSON.stringify(normalizedPayload), [normalizedPayload]);

  // What is on screen right now, for a settling attempt to compare itself against.
  openAttemptRef.current = { encounterId, signature };

  const suspendAutosave = useCallback(() => {
    suspendedRef.current = true;
    suspendedEncounterIdRef.current = encounterId;
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    setScheduleNonce((n) => n + 1);
  }, [encounterId]);

  const resumeAutosave = useCallback(
    (options?: { acceptedCodes: { diagnoses: CodeInput[]; procedures: CodeInput[] } }) => {
      if (options && suspendedEncounterIdRef.current === encounterId) {
        const acceptedSignature = JSON.stringify(
          buildPayload({
            encounterId,
            diagnoses: options.acceptedCodes.diagnoses,
            procedures: options.acceptedCodes.procedures,
          }),
        );
        commitSavedSignature(acceptedSignature);
      }
      suspendedRef.current = false;
      setStrandedSignature(null);
      setScheduleNonce((n) => n + 1);
    },
    [encounterId, commitSavedSignature],
  );

  const scheduleSave = useCallback(() => {
    if (closedToCodeChanges) return;
    if (!hasBaseline) return;
    if (suspendedRef.current) return;
    if (!enabled) return;
    if (signature === lastSignatureRef.current) return;
    // Prevent scheduling a new save if one is already in progress
    if (saveCodes.isPending) {
      return;
    }

    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(async () => {
      const attempt = { id: ++requestIdRef.current, encounterId, signature };
      setInFlightSignature(signature);
      setOutstandingSaves((n) => n + 1);
      setStrandedSignature(null);

      const settled = await saveCodes
        .mutateAsync(normalizedPayload)
        .then(() => ({ landed: true }) as const)
        .catch((error: unknown) => ({ landed: false, error }) as const);

      setOutstandingSaves((n) => n - 1);

      // `signature` carries the encounter id, so a superseded payload covers both a fresh edit and a
      // change of encounter. The two narrower checks stay for the writes that describe this
      // encounter's saved state: they must not land on another encounter, nor behind a newer request.
      const superseded = attempt.signature !== openAttemptRef.current.signature;
      const sameEncounter = attempt.encounterId === openAttemptRef.current.encounterId;
      const isNewest = attempt.id === requestIdRef.current;

      // Cleared whatever settled: leaving the marker set would read as `saving` if the coder came
      // back to these codes.
      if (isNewest) setInFlightSignature(null);

      if (settled.landed && sameEncounter && isNewest) {
        commitSavedSignature(signature);
        retriedSignatureRef.current = null;
        setHasSaved(true);
      }

      if (!settled.landed) {
        // Deliberately not on the per-payload retry cap below: that cap is keyed on the signature,
        // which every edit burst changes, so a refused review would be retried and re-toasted
        // forever.
        if (settled.error instanceof HttpStatusError && settled.error.status === 409) {
          toast.error("Autosave stopped: this review's state changed elsewhere. Reload to see it.");
          setClosedEncounterId(attempt.encounterId);
          // Re-armed even though this attempt is terminal: `scheduleSave` is the only place that
          // decides, so a refusal for the encounter left behind cannot strand the one now open.
          setScheduleNonce((n) => n + 1);
          return;
        }

        // Retries are capped at one attempt per payload: uncapped, a down endpoint is retried every
        // debounce. The cap only governs the payload it failed on, so a superseded one cannot use it
        // to withhold the re-arm from whatever is on screen now.
        const alreadyRetried = retriedSignatureRef.current === signature;
        retriedSignatureRef.current = signature;

        const givenUp = !superseded && alreadyRetried;
        const willRetry = sameEncounter && !givenUp;
        toast.error(
          willRetry ? "Couldn't save your changes. Retrying." : "Couldn't save. Your changes are still unsaved.",
        );

        if (givenUp) {
          setStrandedSignature(signature);
          return;
        }
      }

      setScheduleNonce((n) => n + 1);
    }, debounceMs);
  }, [
    closedToCodeChanges,
    hasBaseline,
    enabled,
    encounterId,
    signature,
    debounceMs,
    saveCodes,
    normalizedPayload,
    commitSavedSignature,
  ]);

  // Depending on `scheduleSave` instead would re-arm the debounce on every render, because
  // `saveCodes` from `useMutation` is a fresh object each time. Accepting Biome's fix here is a
  // silent regression no test catches.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are deliberate; see above
  useEffect(() => {
    scheduleSave();
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [signature, enabled, debounceMs, hasBaseline, scheduleNonce]);

  useEffect(() => {
    const onBeforeUnload = () => {
      if (closedToCodeChanges) return;
      if (!hasBaseline) return;
      if (suspendedRef.current) return;
      if (!enabled) return;
      if (signature !== lastSignatureRef.current) {
        if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
        // Fire-and-forget best-effort save
        saveCodes.mutate(normalizedPayload);
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [closedToCodeChanges, hasBaseline, enabled, signature, saveCodes, normalizedPayload]);

  const saveState = deriveSaveState({
    signature,
    savedSignature,
    inFlightSignature,
    strandedSignature,
    closedToCodeChanges,
    hasSaved,
  });

  // Distinct from `saveState === "saving"`, which follows the edit on screen: a coder who types
  // during a request sees `unsaved`, but the request is still outstanding and can still land.
  return { saveState, isSaveInFlight: outstandingSaves > 0, suspendAutosave, resumeAutosave } as const;
}
